import type {
  CreateTrialAccountCommand,
    CancelRegistrationCheckoutResult,
  CreateRegistrationSessionResult,
  GetRegistrationStatusResult,
  StartRegistrationCheckoutCommand,
  StartRegistrationCheckoutResult,
} from "@/domain/model/Registration";
import type { RegistrationService } from "@/domain/port/RegistrationService";
import type { HttpClient } from "@/infrastructure/HttpClient";
import type {
    CancelRegistrationCheckoutResponseDto,
  CreateRegistrationSessionResponseDto,
  CreateTrialAccountResponseDto,
  RegistrationStatusResponseDto,
  StartRegistrationCheckoutResponseDto,
} from "./dto/RegistrationDto";
import {
  createTrialAccountCommandToDto,
  createTrialAccountResponseDtoToResult,
    mapCancelRegistrationCheckoutResponseDtoToResult,
  mapCreateRegistrationSessionResponseDtoToResult,
  registrationStatusResponseDtoToResult,
  startRegistrationCheckoutCommandToDto,
  startRegistrationCheckoutResponseDtoToResult,
} from "./mappers/RegistrationMapper";
import type { TokenBundle } from "@/domain/model/Auth";

export class RegistrationServiceImpl implements RegistrationService {
  constructor(private readonly http: HttpClient) {}

  async createTrialAccount(
    command: CreateTrialAccountCommand,
    signal?: AbortSignal,
  ): Promise<TokenBundle> {
    const dto = await this.http.request<CreateTrialAccountResponseDto>(
      "/api/onboarding/create-trial-account",
      {
        method: "POST",
        body: JSON.stringify(createTrialAccountCommandToDto(command)),
        signal,
      },
      "none",
    );

    return createTrialAccountResponseDtoToResult(dto);
  }

  async startRegistrationCheckout(
    command: StartRegistrationCheckoutCommand,
  ): Promise<StartRegistrationCheckoutResult> {
    const dto = await this.http.request<StartRegistrationCheckoutResponseDto>(
      "/api/onboarding/registration-checkouts",
      {
        method: "POST",
        body: JSON.stringify(startRegistrationCheckoutCommandToDto(command)),
      },
      "none",
    );

    return startRegistrationCheckoutResponseDtoToResult(dto);
  }

  async getRegistrationStatus(
    registrationId: string,
  ): Promise<GetRegistrationStatusResult> {
    const dto = await this.http.request<RegistrationStatusResponseDto>(
      `/api/onboarding/registrations/${registrationId}`,
      { method: "GET" },
      "none",
    );

    return registrationStatusResponseDtoToResult(dto);
  }

  async createRegistrationSession(
    registrationId: string,
  ): Promise<CreateRegistrationSessionResult> {
    const dto = await this.http.request<CreateRegistrationSessionResponseDto>(
      `/api/onboarding/registrations/${registrationId}/session`,
      { method: "POST" },
      "none",
    );

    return mapCreateRegistrationSessionResponseDtoToResult(dto);
  }

  async cancelRegistrationCheckout(
    registrationId: string,
  ): Promise<CancelRegistrationCheckoutResult> {
    const dto = await this.http.request<CancelRegistrationCheckoutResponseDto>(
      `/api/onboarding/registration-checkouts/${registrationId}/cancel`,
      { method: "POST" },
      "none",
    );

    return mapCancelRegistrationCheckoutResponseDtoToResult(dto);
  }

  async resumeRegistrationCheckout(
    registrationId: string,
  ): Promise<StartRegistrationCheckoutResult> {
    const dto = await this.http.request<StartRegistrationCheckoutResponseDto>(
      `/api/onboarding/registration-checkouts/${registrationId}/resume`,
      { method: "POST" },
      "none",
    );

    return startRegistrationCheckoutResponseDtoToResult(dto);
  }
}
